// IntentResolver do GPH — classificação local e determinística.
// Nunca escolhe skill por palavra-chave isolada; ambiguidade vira pergunta.
//
// Contrato de saída:
// {
//   intent, confidence, capabilityId, ambiguity,
//   extractedEntities, missingSlots, suggestedQuestion, options, fallbackOptions
// }

import { CAPABILITIES, listCapabilities } from './gphCapabilityRegistry';
import { GPH_COPY } from './gphCopy';
import { findDrillByGoal } from '../../data/gphDrillCatalog';

const stripAccents = (value = '') =>
  String(value).normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase();

const SCOPE_NAMES = [
  'red dot', 'holo', 'holografica', 'holográfica', '2x', '3x', '4x', '6x', '8x',
  'acog', 'tatica', 'tática', 'hipfire', 'sem mira', 'geral', 'ads', 'giroscopio', 'giroscópio', 'colimador'
];

const GAME_HINTS = [
  { game: 'pubg_mobile', words: ['pubg'] },
  { game: 'free_fire', words: ['free fire', 'freefire', ' ff '] },
  { game: 'codm', words: ['codm', 'cod mobile', 'call of duty'] }
];

const HIGH_CONFIDENCE = 0.85;
const MEDIUM_CONFIDENCE = 0.6;

// ── Expert Intelligence Core: intents deterministicos (PDS §7.2, §7.3, §16) ──
// Detectados ANTES do keyword scoring, com padrões ancorados para evitar
// falso positivo dentro de frases técnicas.

const GREETING_PATTERN = /^(oi+|ola|olá|bom dia|boa tarde|boa noite|salve|e ai|eai|eae|fala|hey|opa|tudo bem\??|td bem\??)[!.?\s]*$/;

const LOW_INTENT_PATTERN = /^(me ajuda(r)?|ajuda|preciso de ajuda|nao sei por onde comecar|não sei por onde começar|por onde comeco|por onde começo)[!.?\s]*$/;

const ASK_CAPABILITIES_PATTERN = /(o que voce faz|o que você faz|como funciona|o que consegue fazer|quais skills|o que da pra fazer|o que dá pra fazer)/;

// Fora de escopo: temas não-FPS. Guard `mentionsFps` impede bloquear frases
// legítimas de jogo que citem essas palavras por acaso.
const OUT_OF_SCOPE_PATTERN = /\b(politica|política|futebol|namoro|receita de|bolo|filme|novela|criptomoeda|bitcoin|eleicao|eleição|religiao|religião|historia do brasil|história do brasil|matematica escolar)\b/;

const FPS_VOCAB_PATTERN = /\b(mira|sensi|sensibilidade|treino|treinar|spray|recoil|gyro|giroscopio|giroscópio|scope|ads|rush|clutch|x1|1v[1-4]|gameplay|video|vídeo|pubg|free fire|codm|arma|mapa|rank|squad)\b/;

const SET_GAME_PATTERN = /\b(agora jogo|meu foco e|meu foco é|meu jogo principal|jogo principal e|jogo principal é|passei a jogar|mudei para|troquei para)\b/;

// Declarações de memória do jogador (PDS §13): método de controle, dedos,
// dispositivo, estilo. Salvas no perfil quando detectadas.
const MEMORY_PATTERNS = [
  { field: 'controlMethod', pattern: /\buso (gyro|giroscopio|giroscópio) (full|total|sempre)\b/, value: 'gyro_full' },
  { field: 'controlMethod', pattern: /\b(nao uso|não uso) (gyro|giroscopio|giroscópio)\b/, value: 'no_gyro' },
  { field: 'fingers', pattern: /\buso (\d) dedos\b/, value: (m) => Number(m[1]) },
  { field: 'device', pattern: /\bmeu (celular|aparelho|dispositivo) e (.+)/, value: (m) => m[2].trim().slice(0, 40) },
  { field: 'device', pattern: /\bmeu (celular|aparelho|dispositivo) é (.+)/, value: (m) => m[2].trim().slice(0, 40) },
  { field: 'playstyle', pattern: /\bsou (rushador|rusher|agressivo)\b/, value: 'rush' },
  { field: 'playstyle', pattern: /\bsou (suporte|support)\b/, value: 'support' },
  { field: 'playstyle', pattern: /\bjogo de sniper\b/, value: 'sniper' }
];

// Ações contextuais: dependem do último resultado (PDS §7.4).
const TRAINING_FROM_CONTEXT_PATTERN = /\b(treinar (essa|esta) situacao|treinar (essa|esta) situação|treinar isso|quero treinar isso|treina isso)\b/;

const VIDEO_VALIDATION_PATTERN = /\b(validar com video|validar com vídeo|validar no video|validar no vídeo|quero validar com video|quero validar com vídeo)\b/;

const detectMemoryStatement = (normalized, rawText) => {
  const memory = {};
  for (const entry of MEMORY_PATTERNS) {
    const match = normalized.match(entry.pattern);
    if (match) {
      memory[entry.field] = typeof entry.value === 'function' ? entry.value(match) : entry.value;
    }
  }
  return Object.keys(memory).length > 0 ? memory : null;
};

const buildResult = (partial) => ({
  intent: 'unknown',
  confidence: 0,
  capabilityId: null,
  ambiguity: false,
  extractedEntities: {},
  missingSlots: [],
  suggestedQuestion: null,
  options: [],
  fallbackOptions: [],
  ...partial
});

const extractEntities = (normalized, rawText) => {
  const entities = {};

  const scope = SCOPE_NAMES.find((name) => normalized.includes(stripAccents(name)));
  if (scope) entities.scope = scope;

  const valueMatch = rawText.match(/\b(\d{1,3}(?:[.,]\d{1,2})?)\b/);
  if (valueMatch) entities.value = Number(valueMatch[1].replace(',', '.'));

  const gameHint = GAME_HINTS.find((hint) =>
    hint.words.some((word) => normalized.includes(stripAccents(word)))
  );
  if (gameHint) entities.game = gameHint.game;

  return entities;
};

const scoreCapabilities = (normalized) => {
  const scores = [];
  for (const cap of listCapabilities()) {
    let hits = 0;
    let strongest = 0;
    for (const keyword of cap.keywords) {
      const normalizedKeyword = stripAccents(keyword);
      if (normalized.includes(normalizedKeyword)) {
        hits += 1;
        strongest = Math.max(strongest, normalizedKeyword.length);
      }
    }
    if (hits > 0) scores.push({ capability: cap, hits, strongest });
  }
  scores.sort((a, b) => (b.hits - a.hits) || (b.strongest - a.strongest));
  return scores;
};

// Pergunta oficial da ambiguidade calculadora × diagnóstico (spec §10).
const CALCULATOR_AMBIGUITY = {
  question:
    'Você já tem uma mira base e quer calcular as outras, ou quer descobrir o ajuste certo porque sua mira está ruim?',
  options: [
    { label: 'Já tenho uma mira base', value: 'has_base' },
    { label: 'Minha mira está ruim', value: 'aim_bad' },
    { label: 'Não sei, quero ajuda', value: 'need_help' }
  ]
};

// Pergunta oficial para valor bruto sem contexto (spec §7.5).
const RAW_VALUE_QUESTION = (scope, value) => ({
  question: `Entendi que você tem um valor de ${scope} em ${value}. O que você quer fazer com esse valor?`,
  options: [
    { label: 'Calcular outras miras', value: 'calc_others' },
    { label: 'Saber se está alto ou baixo', value: 'evaluate' },
    { label: 'Ajustar porque está ruim', value: 'diagnose' },
    { label: 'Validar com vídeo', value: 'validate_video' },
    { label: 'Salvar como configuração boa', value: 'save_config' }
  ]
});

// Objetivo do vídeo (spec §7.3) — mapeia direto para a capability certa da Mi Análise.
export const VIDEO_GOAL_OPTIONS = [
  { label: 'Por que perdi esse x1', value: 'mi_analysis_x1' },
  { label: 'Minha mira estava ruim?', value: 'mi_analysis_aim' },
  { label: 'Minha movimentação foi o problema?', value: 'mi_analysis_movement' },
  { label: 'Minha sensibilidade parece errada?', value: 'mi_analysis_aim' },
  { label: 'Quero análise geral', value: 'mi_analysis_gameplay' },
  { label: 'Outro motivo', value: 'mi_analysis_gameplay' }
];

export const resolveIntent = (rawText = '', { hasVideoAttachment = false } = {}) => {
  const text = String(rawText || '').trim();
  const normalized = stripAccents(text);

  // Vídeo sem contexto: nunca iniciar análise geral automaticamente.
  if (hasVideoAttachment && normalized.length < 4) {
    return buildResult({
      intent: 'video_no_context',
      confidence: 1,
      ambiguity: true,
      missingSlots: ['analysisGoal'],
      suggestedQuestion: GPH_COPY.videoNoContext,
      options: VIDEO_GOAL_OPTIONS
    });
  }

  if (!normalized) {
    return buildResult({ intent: 'unknown', confidence: 0 });
  }

  // ── Intents deterministicos do Expert Core (early-return, PDS §7.2) ──────

  if (GREETING_PATTERN.test(normalized)) {
    return buildResult({ intent: 'greeting', confidence: 1 });
  }

  if (LOW_INTENT_PATTERN.test(normalized)) {
    return buildResult({ intent: 'low_intent', confidence: 1 });
  }

  if (ASK_CAPABILITIES_PATTERN.test(normalized)) {
    return buildResult({ intent: 'ask_capabilities', confidence: 1 });
  }

  const mentionsFps = FPS_VOCAB_PATTERN.test(normalized);

  if (OUT_OF_SCOPE_PATTERN.test(normalized) && !mentionsFps) {
    return buildResult({ intent: 'out_of_scope', confidence: 1 });
  }

  if (TRAINING_FROM_CONTEXT_PATTERN.test(normalized)) {
    return buildResult({ intent: 'training_from_context', confidence: 0.95 });
  }

  if (VIDEO_VALIDATION_PATTERN.test(normalized)) {
    return buildResult({
      intent: 'video_validation_request',
      confidence: 0.95,
      extractedEntities: { hasVideo: hasVideoAttachment }
    });
  }

  const gameHintEarly = GAME_HINTS.find((hint) =>
    hint.words.some((word) => normalized.includes(stripAccents(word)))
  );

  if (SET_GAME_PATTERN.test(normalized) && gameHintEarly) {
    return buildResult({
      intent: 'set_game_preference',
      confidence: 0.95,
      extractedEntities: { game: gameHintEarly.game }
    });
  }

  // Memória só quando a mensagem é declaração pura — se houver pedido/problema
  // junto ("minha mira sobe quando uso 4 dedos"), a skill certa tem prioridade.
  const REQUEST_VOCAB = /\b(quero|preciso|como|ajuda|calcul|trein(ar|o)|analis|mira (ruim|sobe|desce|treme|passa)|problema|corrigir|ajustar)\b/;
  const memoryStatement = detectMemoryStatement(normalized, text);
  if (memoryStatement && !REQUEST_VOCAB.test(normalized)) {
    return buildResult({
      intent: 'memory_statement',
      confidence: 0.9,
      extractedEntities: { memory: memoryStatement, game: gameHintEarly?.game }
    });
  }

  const entities = extractEntities(normalized, text);
  const mentionsCalc = /\bcalcul/.test(normalized) || normalized.includes('calculadora');
  const mentionsAim = normalized.includes('mira') || normalized.includes('sensi');
  const hasBaseValue = typeof entities.value === 'number';

  // Valor bruto de configuração sem objetivo ("Minha Red Dot está 215").
  if (entities.scope && hasBaseValue && !mentionsCalc && !/\b(quero|como|ajuda)\b/.test(normalized)) {
    return buildResult({
      intent: 'raw_config_value',
      confidence: 0.9,
      ambiguity: true,
      extractedEntities: entities,
      missingSlots: ['desiredAction'],
      suggestedQuestion: RAW_VALUE_QUESTION(entities.scope, entities.value).question,
      options: RAW_VALUE_QUESTION(entities.scope, entities.value).options
    });
  }

  // "Quero calcular minha mira" — ambiguidade oficial calculadora × diagnóstico.
  if (mentionsCalc && mentionsAim && !hasBaseValue && !entities.game) {
    return buildResult({
      intent: 'calculator_request',
      confidence: 0.64,
      ambiguity: true,
      extractedEntities: entities,
      missingSlots: ['desiredAction', 'game'],
      suggestedQuestion: CALCULATOR_AMBIGUITY.question,
      options: CALCULATOR_AMBIGUITY.options
    });
  }

  // Cálculo com contexto suficiente (valor base ou jogo explícito).
  if (mentionsCalc && (hasBaseValue || entities.game)) {
    if (entities.game === 'codm') {
      return buildResult({
        intent: 'calculator_request',
        confidence: 1,
        capabilityId: 'calculator_codm_unsupported',
        extractedEntities: entities
      });
    }
    return buildResult({
      intent: 'calculator_request',
      confidence: HIGH_CONFIDENCE,
      capabilityId: entities.game === 'free_fire' ? 'calculator_freefire' : (entities.game === 'pubg_mobile' ? 'calculator_pubg' : null),
      ambiguity: !entities.game,
      extractedEntities: entities,
      missingSlots: entities.game ? [] : ['game']
    });
  }

  // Score geral pelas keywords do registry.
  const scores = scoreCapabilities(normalized);

  if (scores.length === 0) {
    return buildResult({
      intent: 'unknown',
      confidence: 0,
      capabilityId: 'unknown_intent_fallback',
      fallbackOptions: CAPABILITIES.unknown_intent_fallback.nextSteps
    });
  }

  const top = scores[0];
  const second = scores[1];
  const closeCompetition = second && second.hits === top.hits && second.strongest >= top.strongest * 0.8
    && second.capability.adapter !== top.capability.adapter;

  const confidence = closeCompetition
    ? MEDIUM_CONFIDENCE - 0.05
    : (top.hits > 1 || top.strongest >= 8 ? HIGH_CONFIDENCE : MEDIUM_CONFIDENCE + 0.1);

  const capability = top.capability;
  const missingSlots = (capability.requiredSlots || []).filter((slot) => {
    if (slot === 'game') return !entities.game;
    if (slot === 'symptom') return !capability.keywords.some((k) => normalized.includes(stripAccents(k)) && stripAccents(k).length > 6);
    // drillGoal presente quando o texto já descreve uma situação treinável
    // ("quero treinar 1v4 no PUBG") — evita pergunta redundante (PDS §5.2).
    if (slot === 'drillGoal') return !findDrillByGoal(normalized);
    return true;
  });

  return buildResult({
    intent: capability.intents[0] || 'unknown',
    confidence,
    capabilityId: capability.capabilityId,
    ambiguity: closeCompetition,
    extractedEntities: entities,
    missingSlots,
    suggestedQuestion: capability.clarifyingQuestion?.question || null,
    options: capability.clarifyingQuestion?.options || []
  });
};

export { HIGH_CONFIDENCE, MEDIUM_CONFIDENCE };
export default resolveIntent;
